import type { ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ValidationSummary as HubSpotValidationSummary } from '../../../types/hubspot';
import type {
  ValidationSummary as SalesforceValidationSummary,
  SalesforceLookupFieldValidityCounts
} from '../../../types/salesforce';

type AnyValidationSummary =
  | HubSpotValidationSummary
  | SalesforceValidationSummary;

type ScalarCounts = { valid: number; invalid: number; never_valid_for_sync_writes?: boolean };

const SEARCH_ONLY_FIELD_MESSAGE =
  'Is read-only and can be used to search for existing records.';

function renderScalarValidityCounts(
  counts: ScalarCounts,
  theme: Theme
): ReactNode {
  if (counts.never_valid_for_sync_writes === true) {
    return (
      <Typography
        component="span"
        variant="body2"
        display="inline"
        sx={{ color: theme.customColors.themeWarningMain }}
      >
        {SEARCH_ONLY_FIELD_MESSAGE}
      </Typography>
    );
  }

  return (
    <>
      <span style={{ color: theme.customColors.themeSuccess }}>
        {counts.valid} valid
      </span>
      {counts.invalid > 0 && (
        <>
          ,{' '}
          <span
            style={{
              color: theme.customColors.themeDanger
            }}
          >
            {counts.invalid} invalid
          </span>
        </>
      )}
    </>
  );
}

function renderLookupValidityCounts(
  counts: SalesforceLookupFieldValidityCounts,
  theme: Theme
): ReactNode {
  if (counts.never_valid_for_sync_writes === true) {
    return (
      <Typography
        component="span"
        variant="body2"
        display="inline"
        sx={{ color: theme.customColors.themeWarningMain }}
      >
        {SEARCH_ONLY_FIELD_MESSAGE}
      </Typography>
    );
  }

  return (
    <>
      <span style={{ color: theme.customColors.themeSuccess }}>
        {counts.exact_match} exact
      </span>
      {counts.partial > 0 && (
        <>
          ,{' '}
          <span style={{ color: theme.customColors.themePrimary }}>
            {counts.partial} partial
          </span>
        </>
      )}
      {counts.no_match > 0 && (
        <>
          ,{' '}
          <span style={{ color: theme.customColors.themeDanger }}>
            {counts.no_match} no match
          </span>
        </>
      )}
    </>
  );
}

/** True if the summary has at least one field group with entries to show. */
export function validationSummaryHasDisplayContent(
  vs: AnyValidationSummary | null | undefined
): boolean {
  if (!vs) return false;
  return (
    Object.keys(vs.date_fields || {}).length > 0 ||
    Object.keys(vs.datetime_fields || {}).length > 0 ||
    Object.keys(vs.picklist_fields || {}).length > 0 ||
    Object.keys(vs.email_fields || {}).length > 0 ||
    Object.keys(vs.phone_fields || {}).length > 0 ||
    Object.keys(vs.number_fields || {}).length > 0 ||
    Object.keys(vs.text_fields || {}).length > 0 ||
    Object.keys((vs as SalesforceValidationSummary).lookup_fields || {})
      .length > 0
  );
}

export interface CrmFieldTargetForLabel {
  apiName: string;
  label: string;
}

interface CrmValidationSummarySectionProps {
  validationSummary: AnyValidationSummary | null | undefined;
  fieldTargets: CrmFieldTargetForLabel[];
  title: ReactNode;
  description: ReactNode;
}

export default function CrmValidationSummarySection({
  validationSummary,
  fieldTargets,
  title,
  description
}: CrmValidationSummarySectionProps) {
  const theme = useTheme();

  const getFieldLabel = (apiName: string): string => {
    const field = fieldTargets.find((f) => f.apiName === apiName);
    return field?.label || apiName;
  };

  const vs = validationSummary;
  // duplicate check for !vs to avoid TS errors further down
  if (!vs || !validationSummaryHasDisplayContent(vs)) return null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        {title}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={1}
      >
        {description}
      </Typography>

      <Box display="flex" flexDirection="column" gap={1.5}>
        {vs.date_fields && Object.entries(vs.date_fields).length > 0 && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Date Fields:
            </Typography>
            {Object.entries(vs.date_fields).map(([fieldName, counts]) => (
              <Box key={fieldName} pl={2} mb={0.5}>
                <Typography variant="body2" component="div">
                  <strong>{getFieldLabel(fieldName)}</strong>:{' '}
                  {renderScalarValidityCounts(
                    counts as ScalarCounts,
                    theme
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {vs.datetime_fields &&
          Object.entries(vs.datetime_fields).length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                Datetime Fields:
              </Typography>
              {Object.entries(vs.datetime_fields).map(([fieldName, counts]) => (
                <Box key={fieldName} pl={2} mb={0.5}>
                  <Typography variant="body2" component="div">
                    <strong>{getFieldLabel(fieldName)}</strong>:{' '}
                    {renderScalarValidityCounts(
                      counts as ScalarCounts,
                      theme
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

        {vs.picklist_fields &&
          Object.entries(vs.picklist_fields).length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                Picklist Fields:
              </Typography>
              {Object.entries(vs.picklist_fields).map(([fieldName, counts]) => (
                <Box key={fieldName} pl={2} mb={0.5}>
                  <Typography variant="body2" component="div">
                    <strong>{getFieldLabel(fieldName)}</strong>:{' '}
                    {renderScalarValidityCounts(
                      counts as ScalarCounts,
                      theme
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

        {vs.email_fields && Object.entries(vs.email_fields).length > 0 && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Email Fields:
            </Typography>
            {Object.entries(vs.email_fields).map(([fieldName, counts]) => (
              <Box key={fieldName} pl={2} mb={0.5}>
                <Typography variant="body2" component="div">
                  <strong>{getFieldLabel(fieldName)}</strong>:{' '}
                  {renderScalarValidityCounts(
                    counts as ScalarCounts,
                    theme
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {vs.phone_fields && Object.entries(vs.phone_fields).length > 0 && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Phone Fields:
            </Typography>
            {Object.entries(vs.phone_fields).map(([fieldName, counts]) => (
              <Box key={fieldName} pl={2} mb={0.5}>
                <Typography variant="body2" component="div">
                  <strong>{getFieldLabel(fieldName)}</strong>:{' '}
                  {renderScalarValidityCounts(
                    counts as ScalarCounts,
                    theme
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {vs.number_fields && Object.entries(vs.number_fields).length > 0 && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Number Fields:
            </Typography>
            {Object.entries(vs.number_fields).map(([fieldName, counts]) => (
              <Box key={fieldName} pl={2} mb={0.5}>
                <Typography variant="body2" component="div">
                  <strong>{getFieldLabel(fieldName)}</strong>:{' '}
                  {renderScalarValidityCounts(
                    counts as ScalarCounts,
                    theme
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {(vs as SalesforceValidationSummary).lookup_fields &&
          Object.entries(
            (vs as SalesforceValidationSummary).lookup_fields || {}
          ).length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                Lookup Fields:
              </Typography>
              {Object.entries(
                (vs as SalesforceValidationSummary).lookup_fields || {}
              ).map(([fieldName, counts]) => (
                <Box key={fieldName} pl={2} mb={0.5}>
                  <Typography variant="body2" component="div">
                    <strong>{getFieldLabel(fieldName)}</strong>:{' '}
                    {renderLookupValidityCounts(
                      counts as SalesforceLookupFieldValidityCounts,
                      theme
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

        {vs.text_fields && Object.entries(vs.text_fields).length > 0 && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Text Fields (No Duplicates Allowed):
            </Typography>
            {Object.entries(vs.text_fields).map(([fieldName, counts]) => (
              <Box key={fieldName} pl={2} mb={0.5}>
                <Typography variant="body2" component="div">
                  <strong>{getFieldLabel(fieldName)}</strong>:{' '}
                  {renderScalarValidityCounts(
                    counts as ScalarCounts,
                    theme
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
